/**
 * useSMBSignup hook
 *
 * Handles SMB user signup mutation and authentication flow
 */
import { useCallback, useState } from 'react';

import { SignupType } from '@/__generated__/graphql';
import { SignupSessionError, signup } from '@/auth';
import { ErrEmailAlreadyExists, ErrEmailDomainNotAllowed } from '@/core/address';
import { policies } from '@/core/agreements';
import { APIError } from '@/core/api';
import { Tracking, getTracking } from '@/core/tracking';
import { useApp } from '@/hooks/useAppContext';

import { SMBSignupFormData } from '../types';
import { ValidationErrors } from '../utils/validation';

const ERR_INVALID_FIRST_NAME = 'invalid first name';
const ERR_INVALID_LAST_NAME = 'invalid last name';

const TOU_POLICY = policies['terms-of-use'];
const PRIVACY_POLICY = policies.privacy;

export interface UseSMBSignupReturn {
  loading: boolean;
  error: string | null;
  signupSMBUser: (
    data: SMBSignupFormData,
    recaptchaToken: string,
    returnTo?: string
  ) => Promise<ValidationErrors | void>;
}

/**
 * Hook for SMB user signup
 */
export function useSMBSignup(): UseSMBSignupReturn {
  const { graphql } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signupSMBUser = useCallback(
    async (
      data: SMBSignupFormData,
      recaptchaToken: string,
      returnTo?: string
    ): Promise<ValidationErrors | void> => {
      setLoading(true);
      setError(null);

      try {
        const tracking = getTracking() || ({} as Tracking);
        const now = new Date();

        const currentUrl = returnTo || '/dashboard';

        await signup(
          graphql,
          {
            ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
            email: data.email,
            password: data.password,
            first_name: data.first_name.trim(),
            last_name: data.last_name.trim(),
            signup_type: SignupType.Smb,
            landing_url: tracking.landing,
            agreements: [
              {
                policy: TOU_POLICY.apiKey,
                accepted: !!data.accept_privacy_and_terms_of_use,
                updated_at: now,
              },
              {
                policy: PRIVACY_POLICY.apiKey,
                accepted: !!data.accept_privacy_and_terms_of_use,
                updated_at: now,
              },
            ],
          },
          currentUrl // Redirect back to expert profile
        );

        // signup() calls window.location.assign() so execution won't reach here
      } catch (e: unknown) {
        setLoading(false);

        // The account exists but the token step failed: its message tells the
        // user to sign in (ENG-5760).
        if (e instanceof SignupSessionError) {
          setError(e.message);
          throw e;
        }

        // Handle GraphQL errors
        const message = e instanceof APIError ? e.rawError[0]?.message : undefined;
        switch (message) {
          case ErrEmailAlreadyExists.message:
            return { email: 'Email address is already in use' };
          case ErrEmailDomainNotAllowed.message:
            return { email: 'Email domain not allowed to sign up' };
          case ERR_INVALID_FIRST_NAME:
            return { first_name: 'Invalid name' };
          case ERR_INVALID_LAST_NAME:
            return { last_name: 'Invalid name' };
          default:
            setError(message || 'An error occurred during signup');
            throw e;
        }
      }
    },
    [graphql]
  );

  return {
    loading,
    error,
    signupSMBUser,
  };
}
